import type { GridSortModel } from "@mui/x-data-grid"
import { LocationOn } from "@mui/icons-material"
import {
  Alert,
  Box,
  Container,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { Search } from "@mui/icons-material"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { useGetAddressesQuery } from "../../apis/appApi"
import { AddressesGrid } from "./AddressesGrid"
import { AddressDetailDrawer } from "./AddressDetailDrawer"

export const AddressesPage: FC = () => {
  const { t } = useTranslation()

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sortField, setSortField] = useState("dateCreated")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: "success" | "error"
  }>({ open: false, message: "", severity: "success" })

  const { data, isLoading } = useGetAddressesQuery({
    page: page + 1,
    pageSize,
    search: debouncedSearch || undefined,
    sortField,
    sortOrder,
  })

  // Simple debounce for search
  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPage(0)
    // Use timeout for debounce
    const timeout = setTimeout(() => {
      setDebouncedSearch(value)
    }, 300)
    return () => clearTimeout(timeout)
  }

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      setSortField(model[0].field)
      setSortOrder(model[0].sort ?? "desc")
    }
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <LocationOn color="primary" />
          <Typography variant="h5" fontWeight={700}>
            {t("addresses.title")}
          </Typography>
          {data && (
            <Typography variant="body2" color="text.secondary">
              ({t("addresses.count", { count: data.total })})
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder={t("addresses.search")}
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
      </Box>

      {/* Grid */}
      <AddressesGrid
        rows={data?.data ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        loading={isLoading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSortModelChange={handleSortModelChange}
        onRowClick={(id) => setSelectedId(id)}
      />

      {/* Detail Drawer */}
      <AddressDetailDrawer
        addressId={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        onSuccess={(msg) =>
          setSnackbar({ open: true, message: msg, severity: "success" })
        }
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
